import { MELEE } from "@tiny-seas/shared";

/**
 * Whole-body cutlass choreography layered over the procedural rig, keyed to the
 * same authoritative phases as the blade hand (`sampleSwordPoseToRef`): ready at
 * the end of the windup, impact at the active midpoint, follow-through at the end
 * of the active window, and back to neutral at the end of recovery.
 *
 * Cut 1 is a forehand diagonal (coil right, unwind left onto the lead foot), cut 2
 * the backhand return (coiled left, rear foot steps through), and cut 3 an
 * overhead finisher: rise and lean back, then lunge onto a long lead-foot step.
 */
export interface SwordBody {
  /** Chest yaw about the hips; + turns the chest toward the right hand (+x). */
  twist: number;
  /** Extra forward lean about the hips, radians. */
  lean: number;
  /** Side bend; + raises the right shoulder (weight onto the left side). */
  roll: number;
  /** Hip drop into bent knees, metres. */
  drop: number;
  /** Hips forward of the feet root, metres. */
  lunge: number;
  /** Planted-frame forward reach of the left (lead) and right (rear) feet, metres. */
  leadFoot: number;
  rearFoot: number;
  /** Foot clearance while a foot steps, metres. */
  leadLift: number;
  rearLift: number;
  /** Off (left) hand action-frame offset, metres; −x is outward. */
  offX: number;
  offY: number;
  offZ: number;
  /** First-person view kick: radians (yaw + right, pitch + down, roll) and metres. */
  viewYaw: number;
  viewPitch: number;
  viewRoll: number;
  viewPush: number;
  viewDrop: number;
}

type Key = Exclude<keyof SwordBody, "leadLift" | "rearLift">;
type Frame = Partial<Record<Key, number>>;

const KEYS: readonly Key[] = [
  "twist", "lean", "roll", "drop", "lunge", "leadFoot", "rearFoot", "offX", "offY", "offZ",
  "viewYaw", "viewPitch", "viewRoll", "viewPush", "viewDrop",
];
const FOOT_LIFT = 0.09;

/** Ready / impact / follow per combo step; every unlisted channel is 0. */
const CUTS: readonly { ready: Frame; impact: Frame; follow: Frame }[] = [
  {
    // Forehand diagonal: coil onto the rear foot, unwind left over a short lead step.
    ready: { twist: 0.42, lean: -0.04, roll: -0.07, drop: 0.02, lunge: -0.03, offX: 0.08, offY: 0.05, offZ: 0.04, viewYaw: 0.03, viewPitch: -0.012, viewRoll: -0.02 },
    impact: { twist: -0.14, lean: 0.12, roll: 0.09, drop: 0.05, lunge: 0.08, leadFoot: 0.18, offX: -0.18, offY: 0.03, offZ: -0.22, viewYaw: -0.045, viewPitch: 0.03, viewRoll: 0.035, viewPush: 0.03 },
    follow: { twist: -0.50, lean: 0.14, roll: 0.11, drop: 0.05, lunge: 0.09, leadFoot: 0.18, offX: -0.22, offY: 0.01, offZ: -0.26, viewYaw: -0.05, viewPitch: 0.025, viewRoll: 0.03, viewPush: 0.03 },
  },
  {
    // Backhand return: start coiled left, drive the right hip through and step the rear foot up.
    ready: { twist: -0.46, lean: 0.06, roll: 0.07, drop: 0.04, lunge: 0.02, offX: -0.12, offY: 0.02, offZ: -0.16, viewYaw: -0.035, viewPitch: -0.01, viewRoll: 0.02 },
    impact: { twist: 0.16, lean: 0.10, roll: -0.11, drop: 0.05, lunge: 0.07, rearFoot: 0.20, offX: -0.20, offY: 0.11, offZ: -0.30, viewYaw: 0.05, viewPitch: 0.03, viewRoll: -0.04, viewPush: 0.03 },
    follow: { twist: 0.54, lean: 0.10, roll: -0.13, drop: 0.05, lunge: 0.07, rearFoot: 0.20, offX: -0.24, offY: 0.13, offZ: -0.32, viewYaw: 0.055, viewPitch: 0.025, viewRoll: -0.035, viewPush: 0.03 },
  },
  {
    // Overhead finisher: rise and draw back, then lunge onto a long lead step and chop down through.
    ready: { twist: 0.18, lean: -0.20, roll: -0.04, drop: -0.02, lunge: -0.08, offX: 0.06, offY: 0.16, offZ: 0.08, viewYaw: 0.012, viewPitch: -0.07, viewRoll: -0.012, viewPush: -0.04, viewDrop: -0.02 },
    impact: { twist: -0.05, lean: 0.40, roll: 0.04, drop: 0.16, lunge: 0.24, leadFoot: 0.42, offX: -0.20, offY: -0.08, offZ: -0.34, viewYaw: -0.012, viewPitch: 0.12, viewRoll: 0.018, viewPush: 0.16, viewDrop: 0.08 },
    follow: { twist: -0.14, lean: 0.46, roll: 0.05, drop: 0.18, lunge: 0.26, leadFoot: 0.42, offX: -0.22, offY: -0.10, offZ: -0.36, viewYaw: -0.012, viewPitch: 0.13, viewRoll: 0.015, viewPush: 0.17, viewDrop: 0.09 },
  },
];

/** Longest struck flinch; presenters stop advancing a flinch clock past it. */
export const SWORD_FLINCH_SECONDS = 0.6;
/** Struck flinch per incoming cut: snaps away from the blade, the finisher knocks the body back. */
const FLINCH: readonly { seconds: number; frame: Frame }[] = [
  { seconds: 0.42, frame: { twist: -0.24, lean: -0.16, roll: 0.08, drop: 0.05, lunge: -0.07, offX: -0.08, offY: 0.10, offZ: -0.08, viewYaw: -0.03, viewPitch: -0.05, viewRoll: 0.04 } },
  { seconds: 0.42, frame: { twist: 0.24, lean: -0.16, roll: -0.08, drop: 0.05, lunge: -0.07, offX: -0.08, offY: 0.10, offZ: -0.08, viewYaw: 0.03, viewPitch: -0.05, viewRoll: -0.04 } },
  { seconds: SWORD_FLINCH_SECONDS, frame: { lean: -0.30, drop: 0.10, lunge: -0.13, offX: -0.14, offY: 0.16, offZ: -0.10, viewPitch: -0.09, viewPush: -0.08, viewDrop: 0.04 } },
];
const FLINCH_RISE = 0.07;

const smooth = (t: number): number => { const v = Math.max(0, Math.min(1, t)); return v * v * (3 - 2 * v); };

export function createSwordBody(): SwordBody {
  return {
    twist: 0, lean: 0, roll: 0, drop: 0, lunge: 0, leadFoot: 0, rearFoot: 0, leadLift: 0, rearLift: 0,
    offX: 0, offY: 0, offZ: 0, viewYaw: 0, viewPitch: 0, viewRoll: 0, viewPush: 0, viewDrop: 0,
  };
}

/**
 * Body offsets `elapsed` seconds into combo `step` (−1 or past recovery: neutral),
 * plus the flinch of a cut taken `hitAge` seconds ago (−1: none).
 */
export function sampleSwordBodyToRef(step: number, elapsed: number, hitStep: number, hitAge: number, out: SwordBody): SwordBody {
  const index = step === 1 || step === 2 ? step : 0;
  const timing = MELEE.combo[index]!, cut = CUTS[index]!;
  const impact = timing.windup + timing.active / 2, follow = timing.windup + timing.active;
  let from: Frame | null = null, to: Frame | null = null, blend = 0;
  if (elapsed >= 0 && elapsed < timing.windup) { to = cut.ready; blend = smooth(elapsed / timing.windup); }
  else if (elapsed >= timing.windup && elapsed < impact) { from = cut.ready; to = cut.impact; blend = smooth((elapsed - timing.windup) / (impact - timing.windup)); }
  else if (elapsed >= impact && elapsed < follow) { from = cut.impact; to = cut.follow; blend = smooth((elapsed - impact) / (follow - impact)); }
  else if (elapsed >= follow) { from = cut.follow; blend = smooth((elapsed - follow) / timing.recovery); }
  for (const key of KEYS) {
    const a = from?.[key] ?? 0, b = to?.[key] ?? 0;
    out[key] = a + (b - a) * blend;
  }
  // Only the stepping foot travels from 0 to a held peak and back, so its bell lifts mid-step.
  const leadPeak = cut.follow.leadFoot ?? 0, rearPeak = cut.follow.rearFoot ?? 0;
  out.leadLift = leadPeak > 0 ? FOOT_LIFT * Math.sin(Math.PI * Math.max(0, Math.min(1, out.leadFoot / leadPeak))) : 0;
  out.rearLift = rearPeak > 0 ? FOOT_LIFT * Math.sin(Math.PI * Math.max(0, Math.min(1, out.rearFoot / rearPeak))) : 0;
  if (hitAge >= 0) {
    const flinch = FLINCH[hitStep === 1 || hitStep === 2 ? hitStep : 0]!;
    if (hitAge < flinch.seconds) {
      const weight = hitAge < FLINCH_RISE ? smooth(hitAge / FLINCH_RISE) : 1 - smooth((hitAge - FLINCH_RISE) / (flinch.seconds - FLINCH_RISE));
      for (const key of KEYS) out[key] += (flinch.frame[key] ?? 0) * weight;
    }
  }
  return out;
}
